// @ts-nocheck
import { useShellContext } from "@insite/client-framework/Components/IsInShell";
import Zone from "@insite/client-framework/Components/Zone";
import { HasFields } from "@insite/client-framework/Types/ContentItemModel";
import { ChildFieldDefinition } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import useCurrentBreakpointIndex from "@store/Framework/Common/Hooks/useCurrentBreakpointIndex";
import React from "react";

const enum fields {
    zones = "zones",
    zoneToEdit = "zoneToEdit",
}
const breakpointNames: Partial<keyof ZoneConfig["fields"]>[] = [
    "Small Mobile",
    "Mobile",
    "Tablet",
    "Desktop",
    "Large Desktop",
];

interface ZoneConfig {
    fields: {
        zoneName: string;
        "Small Mobile": boolean;
        Mobile: boolean;
        Tablet: boolean;
        Desktop: boolean;
        "Large Desktop": boolean;
    };
}

interface Props extends WidgetProps {
    fields: {
        [fields.zones]: ZoneConfig[];
        [fields.zoneToEdit]: string;
    };
}

const ResponsiveZones: React.FC<Props> = ({ fields: { zones, zoneToEdit }, id }) => {
    const currentBreakpoint = useCurrentBreakpointIndex();
    const isEditing = useShellContext().isEditing;
    const shouldUseCurrentBreakpoint = zoneToEdit === "currentBreakpoint";
    if (!zones.length || currentBreakpoint === null) {
        return null;
    }
    const shellBreakpoint = shouldUseCurrentBreakpoint ? currentBreakpoint : parseInt(zoneToEdit);
    const breakPointToUse = isEditing ? shellBreakpoint : currentBreakpoint;

    const currentBreakpointName = breakpointNames[breakPointToUse];

    const currentZone = shouldUseCurrentBreakpoint
        ? zones.find(zone => zone.fields[currentBreakpointName] === true)
        : zones.find(zone => zone.fields.zoneName === zoneToEdit);
    if (!currentZone) {
        return null;
    }

    return <Zone contentId={id} zoneName={currentZone.fields.zoneName} />;
};

const checkBoxFields: ChildFieldDefinition[] = breakpointNames.map((name, index) => ({
    name: breakpointNames[index],
    displayName: name,
    editorTemplate: "CheckboxField",
    defaultValue: false,
}));

const widgetModule: WidgetModule<Bunzl.BunzlDefinition> = {
    component: ResponsiveZones,
    definition: {
        group: "Basic",
        icon: "border-all",
        fieldDefinitions: [
            {
                name: fields.zones,
                displayName: "Zone Configurations",
                editorTemplate: "ListField",
                fieldType: "General",
                defaultValue: [],
                validate(_: any, item: HasFields) {
                    const seenBreakpoints: { [key: string]: boolean } = {};
                    const duplicateBreakPoints: string[] = [];
                    const zones: ZoneConfig[] = item.fields.zones;

                    zones.forEach(({ fields: zone }) => {
                        breakpointNames.forEach((breakpointName, index) => {
                            if (zone[breakpointName as keyof ZoneConfig["fields"]] === true) {
                                if (seenBreakpoints[breakpointName]) {
                                    duplicateBreakPoints.push(breakpointName);
                                }
                                seenBreakpoints[breakpointName] = true;
                            }
                        });
                    });

                    if (duplicateBreakPoints.length) {
                        return `The following breakpoints are assigned to multiple zones: ${duplicateBreakPoints.join(
                            ", ",
                        )}`;
                    }
                    return null;
                },
                getDisplay(item) {
                    return item.fields.zoneName;
                },
                fieldDefinitions: [
                    {
                        name: "zoneName",
                        displayName: "Zone Name",
                        editorTemplate: "TextField",
                        defaultValue: "Zone1",
                    },
                    ...checkBoxFields,
                ],
            },
            {
                name: fields.zoneToEdit,
                displayName: "Zone to Edit",
                hideEmptyOption: true,
                editorTemplate: "DropDownWithDynamicOptionsField",
                fieldType: "General",
                defaultValue: "currentBreakpoint",
                options: async items => {
                    const zoneOptions = items?.fields.zones.map((zone: ZoneConfig) => ({
                        value: zone.fields.zoneName,
                        displayName: zone.fields.zoneName,
                    }));
                    const options = [{ value: "currentBreakpoint", displayName: "Current Breakpoint" }, ...zoneOptions];
                    return options;
                },
            },
        ],
    },
};

export default widgetModule;
